import {
  useMutation,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { useAuthenticatedFetch } from "@/hooks/use-authenticated-fetch";
import { ApiError } from "@/lib/api";
import { projectKeys, recordKeys } from "@/lib/query-keys";
import { invalidateReportContents } from "@/lib/invalidate-report-contents";
import {
  updateCachedRecord,
  type RecordCacheEntry,
} from "@/lib/records/record-cache";
import type { ProjectImage, ProjectRecord } from "@/types/api";

interface DeleteRecordImageVars {
  projectId: string;
  recordId: string;
  imageId: string;
}

interface DeleteRecordImageContext {
  snapshots: ReadonlyArray<readonly [QueryKey, RecordCacheEntry | undefined]>;
}

/**
 * Drop one photo from a record's image set in a cached copy of the record,
 * across BOTH the v2.0 `images[]` shape and the legacy single `image`. Mirrors
 * the [[record-images]] seam so the optimistic patch leaves the cover correct:
 * - `images[]` present → filter the removed `imageId` out; when that empties the
 *   array, also clear the legacy `image` if it pointed at the same photo so a
 *   stale cover can't linger.
 * - no `images[]` (legacy single-image record) → drop the top-level `image`
 *   when it is the photo being removed. A record dropping to its last photo
 *   still resolves a cover via the surviving `images[0]` / `image`.
 */
function removeImageFromRecord(
  record: ProjectRecord,
  imageId: string,
): ProjectRecord {
  const nextImages: ProjectImage[] | undefined = record.images
    ? record.images.filter((img) => img.id !== imageId)
    : undefined;
  // Keep the legacy cover in lockstep: clear it when it IS the removed photo,
  // or when the v2.0 set just emptied (so no orphaned cover survives).
  const legacyImageRemoved =
    record.image?.id === imageId ||
    (nextImages !== undefined && nextImages.length === 0);
  return {
    ...record,
    images: nextImages,
    image: legacyImageRemoved ? undefined : record.image,
  };
}

/**
 * Optimistic single-photo delete for a SYNCED multi-photo record (US-014,
 * online-only). Modelled on `useBulkDeleteRecords` — the canonical optimistic
 * cache pattern in this repo: cancel in-flight list queries, snapshot every
 * matching records-list cache (one per filter combination), patch each page so
 * the target record drops the photo, then roll back on error / invalidate on
 * settle.
 *
 * `DELETE /projects/2.0/projects/{projectId}/records/{recordId}/images/{imageId}/`
 * returns the full v2.0 record body. The verb is HTTP-idempotent, so a **404 is
 * treated as success** — the photo is already gone (a retry whose first response
 * was lost on a flaky connection must not surface as failure). A 409 ("last
 * photo") shouldn't occur — the lone-photo "×" routes to record-delete before
 * reaching here — but it propagates so the caller can surface it defensively.
 *
 * The optimistic patch is held in cache until the `onSettled` refetch lands (we
 * do NOT clear it early) so the deleted photo never flickers back. The hook owns
 * no UI — the caller passes `onError` / `onSuccess` via the standard mutate
 * callbacks per [[mutations]].
 */
export function useDeleteRecordImage() {
  const fetchWithAuth = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    DeleteRecordImageVars,
    DeleteRecordImageContext
  >({
    mutationFn: async ({ projectId, recordId, imageId }) => {
      try {
        await fetchWithAuth<ProjectRecord>(
          `/projects/2.0/projects/${projectId}/records/${recordId}/images/${imageId}/`,
          { method: "DELETE" },
        );
      } catch (err) {
        // 404 = "photo is already gone" → semantically successful for an
        // idempotent DELETE. A retry whose first response was lost on a flaky
        // connection sees 404 and must NOT surface as failure. Everything else
        // (incl. the 409 last-photo case) rejects for the caller to handle.
        if (err instanceof ApiError && err.status === 404) return;
        throw err;
      }
    },
    onMutate: async ({ projectId, recordId, imageId }) => {
      // Cancel in-flight fetches for any matching record-list query so they
      // can't overwrite the optimistic patch with stale data.
      await queryClient.cancelQueries({
        queryKey: recordKeys.byProject(projectId),
      });

      // Snapshot every matching cache (one per filter combination) so rollback
      // can restore them if the server rejects the delete.
      const queries = queryClient.getQueriesData<RecordCacheEntry>({
        queryKey: recordKeys.byProject(projectId),
      });
      const snapshots = queries.map(
        ([key, data]) => [key, data ? structuredClone(data) : undefined] as const,
      );

      // Patch each cache: drop the photo from the target record on every list
      // page, and from the record's own entry (a photo opened from a report).
      for (const [key] of queries) {
        queryClient.setQueryData<RecordCacheEntry>(key, (old) =>
          updateCachedRecord(old, recordId, (record) =>
            removeImageFromRecord(record, imageId),
          ),
        );
      }

      return { snapshots };
    },
    onError: (_err, _vars, context) => {
      if (!context) return;
      for (const [key, data] of context.snapshots) {
        queryClient.setQueryData(key, data);
      }
    },
    onSettled: (_data, _err, { projectId }) => {
      // Refetch the authoritative record list — the photo-count change settles
      // here. The optimistic patch stays in cache until this lands, so the
      // deleted photo never flickers back.
      void queryClient.invalidateQueries({
        queryKey: recordKeys.byProject(projectId),
      });
      // Project list & detail carry per-record photo counts; flush both to match
      // `useBulkDeleteRecords`'s minimal invalidation set.
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
      // A photos report counts photos, and its tile shows the cover.
      invalidateReportContents(queryClient, projectId, { counts: true });
    },
  });
}
